import gql from 'graphql-tag';
import {isLeafType} from 'graphql/type';
import {validate} from 'graphql/validation';
import {TypeInfo, visitWithTypeInfo, astFromValue} from 'graphql/utilities';
import {Kind, visit} from 'graphql/language';
import {weakMemo} from '@packages/utilities/memo';
import {schema as graphqlSchema} from '@packages/systems/cms/graphql';
import {isCmsItemTypeName} from '../utils/isCmsItemTypeName';

import type {QueryAST} from '../types';
import type {GraphQLError} from 'graphql/error';
import type {
  GraphQLArgument,
  GraphQLField,
  GraphQLOutputType,
  GraphQLSchema,
} from 'graphql/type';
import type {
  ArgumentNode,
  ASTNode,
  DefinitionNode,
  DocumentNode,
  FieldNode,
  ObjectValueNode,
  SelectionNode,
  SelectionSetNode,
} from 'graphql/language';

/*
  Since we generate the GraphQL queries dynamically at run-time, we must be careful
  that the generated queries follow the GraphQL schema. This is much less of an issue
  with the static GraphQL schema introduced circa 2025. Generated queries must also
  handle edge cases properly, such as omitting variable definitions that do not have
  corresponding variables used in the query. The current query generation logic does
  not consistently handle this requirement. It's possible that our query generation
  results in GraphQL queries that have invalid fields or arguments. Apollo client
  throws an error if the requested query is invalid so we validate the query and
  modify it to delete all invalid fields prior to passing the query to the client.
 */
export const parseQueryAST = (
  schema: GraphQLSchema,
  queryDocumentString: string
): QueryAST | null => {
  const queryAST = addDefaultSelectionsToQueryAST(gql`
    ${queryDocumentString}
  `);

  const validatedQueryAST = toValidQueryAST(schema)(queryAST);

  if (isEmptyQueryDocument(validatedQueryAST)) {
    // Sometimes, generated query is so corrupt that after the validation,
    // there is nothing left. In these cases we return null instead of an
    // empty query.
    return null;
  }

  return validatedQueryAST;
};

/**
 * Now that we don't call the introspection query,
 * manually add basic subfields to the query AST that are not included in the binding sources.
 */
const addDefaultSelectionsToQueryAST = weakMemo(
  (queryDocument: QueryAST): QueryAST => {
    const typeInfo = new TypeInfo(graphqlSchema);
    return visit(
      queryDocument,
      visitWithTypeInfo(typeInfo, {
        [Kind.FIELD]: {
          enter(node: FieldNode) {
            const type = getRelevantFieldType(typeInfo);

            if (!type) {
              return null;
            }

            // This makes sure all the GraphQL queries
            // contain all the needed subfields.
            if (
              !isLeafType(type) &&
              // In case of an item field, we don't want to fetch any of the
              // subfields if they weren't in the original query document.
              // The `leave` step makes sure we include the item ID subfield.
              !isCmsItemType(type) &&
              !isSkuItemType(type) &&
              !node.selectionSet &&
              'getFields' in type
            ) {
              return {
                ...node,
                selectionSet: {
                  kind: Kind.SELECTION_SET,
                  selections: Object.values(type.getFields()).map(
                    fieldFromFieldDef
                  ),
                },
              };
            }
          },
          leave(node: FieldNode) {
            const type = getRelevantFieldType(typeInfo);
            const fieldDef = typeInfo.getFieldDef();

            if (!type || !fieldDef) {
              return null;
            }

            // Apollo normalizes its cache using object IDs. This makes sure we always add
            // the ID field in a query selection set if the ID subfield is in the schema.
            const idFieldDef =
              !isLeafType(type) && 'getFields' in type && type.getFields().id;
            const needsIdFieldDef =
              idFieldDef &&
              (!node.selectionSet ||
                isSelectionSetWithoutIdSelection(node.selectionSet));

            // Apollo normalizes its cache using object IDs combined with cmsLocaleIds. This
            // makes sure we always add the cmsLocaleId field in a query selection set if the
            // cmsLocaleId subfield is in the schema.
            const cmsLocaleIdFieldDef =
              !isLeafType(type) &&
              'getFields' in type &&
              type.getFields().cmsLocaleId;
            const needsCMSLocaleIdFieldDef =
              cmsLocaleIdFieldDef &&
              (!node.selectionSet ||
                isSelectionSetWithoutCMSLocaleIdSelection(node.selectionSet));

            const draftFieldDef =
              isNonRootCmsItemField(node) &&
              'getFields' in type &&
              type.getFields()['draft'];

            const archivedFieldDef =
              isNonRootCmsItemField(node) &&
              'getFields' in type &&
              type.getFields()['archived'];

            const filterArgumentDef =
              isCmsItemConnectionType(type) &&
              getArgumentDef(fieldDef, 'filter');

            const args = node.arguments ? [...node.arguments] : [];
            let filterArgument = getArgument(args, 'filter');
            const needsFilterArgumentDef =
              filterArgumentDef && !isSkuItemType(type) && !filterArgument;

            const needsDraftArgumentDef =
              filterArgumentDef &&
              !isSkuItemType(type) &&
              (!filterArgument ||
                !hasInputObjectArgumentKey(filterArgument, 'draft'));

            const needsArchivedArgumentDef =
              filterArgumentDef &&
              !isSkuItemType(type) &&
              (!filterArgument ||
                !hasInputObjectArgumentKey(filterArgument, 'archived'));

            if (
              needsIdFieldDef ||
              draftFieldDef ||
              archivedFieldDef ||
              needsFilterArgumentDef ||
              needsDraftArgumentDef ||
              needsArchivedArgumentDef ||
              needsCMSLocaleIdFieldDef
            ) {
              const selections = node.selectionSet
                ? [...node.selectionSet.selections]
                : [];

              if (needsFilterArgumentDef) {
                const value: Record<string, unknown> = {};

                if (needsArchivedArgumentDef) {
                  value.archived = false;
                }

                if (needsDraftArgumentDef) {
                  value.draft = false;
                }

                args.push(argumentFromArgumentDef(filterArgumentDef, value));
              } else {
                if (needsArchivedArgumentDef) {
                  filterArgument = getArgument(args, 'filter');

                  if (
                    filterArgument &&
                    filterArgument.value.kind === Kind.OBJECT
                  ) {
                    const updatedFilterArgument: ArgumentNode = {
                      kind: Kind.ARGUMENT,
                      name: filterArgument.name,
                      value: {
                        kind: Kind.OBJECT,
                        fields: [
                          ...filterArgument.value.fields,
                          {
                            kind: Kind.OBJECT_FIELD,
                            name: {kind: Kind.NAME, value: 'archived'},
                            value: {kind: Kind.BOOLEAN, value: false},
                          },
                        ],
                      },
                    };

                    const argIndex = args.findIndex(
                      (arg) => arg.name && arg.name.value === 'filter'
                    );

                    if (argIndex !== -1) {
                      args[argIndex] = updatedFilterArgument;
                    }
                  }
                }

                if (needsDraftArgumentDef) {
                  filterArgument = getArgument(args, 'filter');

                  if (
                    filterArgument &&
                    filterArgument.value.kind === Kind.OBJECT
                  ) {
                    const updatedFilterArgument: ArgumentNode = {
                      kind: Kind.ARGUMENT,
                      name: filterArgument.name,
                      value: {
                        kind: Kind.OBJECT,
                        fields: [
                          ...filterArgument.value.fields,
                          {
                            kind: Kind.OBJECT_FIELD,
                            name: {kind: Kind.NAME, value: 'draft'},
                            value: {kind: Kind.BOOLEAN, value: false},
                          },
                        ],
                      },
                    };

                    const argIndex = args.findIndex(
                      (arg) => arg.name && arg.name.value === 'filter'
                    );

                    if (argIndex !== -1) {
                      args[argIndex] = updatedFilterArgument;
                    }
                  }
                }
              }

              if (archivedFieldDef) {
                selections.unshift(fieldFromFieldDef(archivedFieldDef));
              }

              if (draftFieldDef) {
                selections.unshift(fieldFromFieldDef(draftFieldDef));
              }

              if (needsCMSLocaleIdFieldDef) {
                selections.unshift(fieldFromFieldDef(cmsLocaleIdFieldDef));
              }

              if (needsIdFieldDef) {
                selections.unshift(fieldFromFieldDef(idFieldDef));
              }

              return {
                ...node,
                arguments: args,
                selectionSet: {
                  kind: Kind.SELECTION_SET,
                  selections,
                },
              };
            }
          },
        },
      })
    );
  }
);

const toValidQueryAST = weakMemo((schema: GraphQLSchema) =>
  weakMemo((queryAst: QueryAST): QueryAST => {
    const errorNodes = getErrorNodes(schema, queryAst);
    return visit(queryAst, {
      enter(node) {
        const isValid = !errorNodes.includes(node);
        // Returning null from this visitor function
        // deletes the node from the AST tree.
        return isValid ? node : null;
      },
      [Kind.OBJECT]: {
        leave(node: ObjectValueNode) {
          // We dont want the validation to leave behind empty objects.
          if (node.fields.length === 0) {
            return null;
          }
        },
      },
    });
  })
);

const isEmptyQueryDocument = (queryDocument: QueryAST): boolean => {
  if (!queryDocument || queryDocument.kind !== Kind.DOCUMENT) {
    return false;
  }
  return queryDocument.definitions.every((definition: DefinitionNode) => {
    return (
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.selectionSet.selections.length === 0
    );
  });
};

const getErrorNodes = (
  schema: GraphQLSchema,
  queryAst: DocumentNode
): ASTNode[] => {
  const errors: ReadonlyArray<GraphQLError> = validate(schema, queryAst);
  return errors.reduce<ASTNode[]>((errorNodes, error) => {
    return error.nodes ? errorNodes.concat(error.nodes) : errorNodes;
  }, []);
};

const getRelevantFieldType = (
  typeInfo: TypeInfo
): GraphQLOutputType | null | undefined => {
  const type = typeInfo.getType();
  if (type && 'ofType' in type && type.ofType) {
    return type.ofType;
  }
  return type;
};

const getArgumentDef = (
  fieldDef: GraphQLField<unknown, unknown>,
  name: string
): GraphQLArgument | undefined =>
  fieldDef.args.find((arg) => arg.name === name);

const getArgument = (
  args: ArgumentNode[],
  name: string
): ArgumentNode | undefined =>
  args.find((arg) => arg.name && arg.name.value === name);

const hasInputObjectArgumentKey = (
  argument: ArgumentNode,
  key: string
): boolean => {
  return (
    argument?.value.kind === Kind.OBJECT &&
    Boolean(argument.value.fields.find((field) => field.name.value === key))
  );
};

const isSelectionSetWithoutIdSelection = (node: SelectionSetNode): boolean => {
  return node.selections.every((selectionNode: SelectionNode) => {
    return selectionNode.kind === Kind.INLINE_FRAGMENT
      ? isSelectionSetWithoutIdSelection(selectionNode.selectionSet)
      : selectionNode.name.value !== 'id';
  });
};

const isSelectionSetWithoutCMSLocaleIdSelection = (
  node: SelectionSetNode
): boolean => {
  return node.selections.every((selectionNode: SelectionNode) => {
    return selectionNode.kind === Kind.INLINE_FRAGMENT
      ? isSelectionSetWithoutCMSLocaleIdSelection(selectionNode.selectionSet)
      : selectionNode.name.value !== 'cmsLocaleId';
  });
};

const fieldFromFieldDef = (
  fieldDef: GraphQLField<unknown, unknown>
): FieldNode => {
  return {
    kind: Kind.FIELD,
    name: {kind: Kind.NAME, value: fieldDef.name},
  };
};

const argumentFromArgumentDef = (
  argumentDef: GraphQLArgument,
  value: unknown
): ArgumentNode => {
  return {
    kind: Kind.ARGUMENT,
    name: {kind: Kind.NAME, value: argumentDef.name},
    value: astFromValue(value, argumentDef.type) || {kind: Kind.NULL},
  };
};

const isSkuItemTypeName = (typeName: string): boolean =>
  typeName === 'CommerceOrderSku';

const isNonRootCmsItemField = (node: FieldNode): boolean => {
  return (
    node.name.value === 'resolveLocalItemRef' ||
    node.name.value === 'resolveLocalItemRefSet' ||
    node.name.value === 'product' ||
    node.name.value === 'sku' ||
    node.name.value === 'defaultSku'
  );
};

const isCmsItemType = (type?: GraphQLOutputType | null): boolean => {
  return Boolean(type && 'name' in type && isCmsItemTypeName(type.name));
};

const isCmsItemConnectionType = (type?: GraphQLOutputType | null): boolean => {
  return Boolean(type && 'name' in type && type.name === 'CMSItemConnection');
};

const isSkuItemType = (type?: GraphQLOutputType | null): boolean => {
  return Boolean(type && 'name' in type && isSkuItemTypeName(type.name));
};
